import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, Chip, Divider,
  Avatar, LinearProgress, Tooltip
} from '@mui/material';
import {
  Save as SaveIcon, CheckCircle, Group, Info, Refresh
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { subjectStudentsAPI, teachersAPI, settingsAPI, subjectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function ClassRegister() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isTeacher = !!user?.teacher_id;

  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]); // teacher's assigned subjects

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  const [data, setData] = useState(null); // { registered, all_enrolled, is_configured }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load meta
  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setSelectedYear(cur.id);
      }
    }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    subjectsAPI.getAll().then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});

    // If teacher: load only their assigned subjects
    if (isTeacher) {
      teachersAPI.getMyAssignments().then(r => {
        if (r.data.success) setAssignments(r.data.data);
      }).catch(() => {});
    }
  }, [isTeacher]);

  // Teacher sees only classes they're assigned to for the selected year
  const availableClasses = isTeacher
    ? classes.filter(c =>
        assignments.some(a => a.class_id == c.id && a.academic_year_id == selectedYear)
      )
    : classes;

  // Teacher sees only subjects they teach in the selected class+year
  const availableSubjects = isTeacher
    ? assignments
        .filter(a => a.class_id == selectedClass && a.academic_year_id == selectedYear)
        .map(a => ({ id: a.subject_id, name: a.subject_name, code: a.subject_code }))
    : subjects;

  const loadRegister = async () => {
    if (!selectedYear || !selectedClass || !selectedSubject) {
      return enqueueSnackbar('Select year, class and subject first', { variant: 'warning' });
    }
    setLoading(true); setData(null);
    try {
      const res = await subjectStudentsAPI.get({
        subject_id: selectedSubject,
        class_id: selectedClass,
        academic_year_id: selectedYear
      });
      if (res.data.success) {
        setData(res.data);
        // Pre-select: if already configured use registered list, else select all
        if (res.data.is_configured) {
          setSelectedIds(new Set(res.data.registered.map(s => s.student_id)));
        } else {
          // Core subjects: select all enrolled by default (all students take core subjects)
          // Elective subjects: also select all by default but admin can deselect
          setSelectedIds(new Set(res.data.all_enrolled.map(s => s.student_id)));
        }
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error loading register', { variant: 'error' });
    } finally { setLoading(false); }
  };

  const toggleStudent = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allStudents = data?.is_configured ? data.registered : data.all_enrolled;
    if (selectedIds.size === allStudents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allStudents.map(s => s.student_id)));
    }
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) return enqueueSnackbar('Select at least one student', { variant: 'warning' });
    setSaving(true);
    try {
      const res = await subjectStudentsAPI.save({
        subject_id: selectedSubject,
        class_id: selectedClass,
        academic_year_id: selectedYear,
        student_ids: Array.from(selectedIds)
      });
      enqueueSnackbar(res.data.message, { variant: 'success' });
      loadRegister(); // refresh
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving register', { variant: 'error' });
    } finally { setSaving(false); }
  };

  const displayStudents = data
    ? (data.is_configured ? data.registered : data.all_enrolled)
    : [];

  const subjectName = availableSubjects.find(s => s.id == selectedSubject)?.name || '';
  const className = classes.find(c => c.id == selectedClass);
  const classLabel = className ? `${className.name}${className.stream ? ' ' + className.stream : ''}` : '';
  const yearLabel = academicYears.find(y => y.id == selectedYear);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>My Class Register</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Set which students you teach for each subject. Done once per year — carries across all terms automatically.
        </Typography>
      </Box>

      {/* How it works */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        <strong>How it works:</strong> Select the academic year, your class and subject, then tick the students you teach that subject.
        Save once — these students will appear in your marks entry sheet for every assessment in that subject for the entire year.
        If you teach all students in the class, just leave them all ticked.
      </Alert>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField select label="Academic Year" value={selectedYear} onChange={e => { setSelectedYear(e.target.value); setData(null); setSelectedSubject(''); }}>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Class" value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setData(null); setSelectedSubject(''); }}>
                <MenuItem value="">Select class…</MenuItem>
                {availableClasses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField select label="Subject" value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setData(null); }}
                disabled={!selectedClass}>
                <MenuItem value="">Select subject…</MenuItem>
                {availableSubjects.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="contained" onClick={loadRegister} disabled={loading || !selectedSubject} fullWidth size="large"
                startIcon={<Refresh />}
                sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' }, fontWeight: 700 }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load Register'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Student list */}
      {data && (
        <>
          {/* Status banner */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={700}>
                {subjectName} — {classLabel}
              </Typography>
              {data.is_configured
                ? <Chip icon={<CheckCircle />} label="Register saved" color="success" size="small" sx={{ fontWeight: 700 }} />
                : <Chip icon={<Group />} label="Not yet saved — showing all enrolled" color="warning" size="small" sx={{ fontWeight: 700 }} />
              }
              <Chip label={`${selectedIds.size} / ${displayStudents.length} selected`} color="primary" size="small" />
            </Box>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              size="large" sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' }, fontWeight: 700, minWidth: 160 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Register'}
            </Button>
          </Box>

          {!data.is_configured && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This register has not been saved yet. All enrolled students are shown. Deselect any students you do NOT teach this subject, then click Save Register.
            </Alert>
          )}

          <Card>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    <TableCell padding="checkbox" sx={{ bgcolor: '#0f2b4a' }}>
                      <Tooltip title={selectedIds.size === displayStudents.length ? 'Deselect all' : 'Select all'}>
                        <Checkbox
                          checked={displayStudents.length > 0 && selectedIds.size === displayStudents.length}
                          indeterminate={selectedIds.size > 0 && selectedIds.size < displayStudents.length}
                          onChange={toggleAll}
                          sx={{ color: 'white', '&.Mui-checked': { color: '#c9952a' }, '&.MuiCheckbox-indeterminate': { color: '#c9952a' } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,.8)', fontWeight: 700, fontSize: 11 }}>Student</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,.8)', fontWeight: 700, fontSize: 11 }}>Admission No.</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,.8)', fontWeight: 700, fontSize: 11 }}>Gender</TableCell>
                    <TableCell sx={{ color: '#c9952a', fontWeight: 700, fontSize: 11 }}>Included in {subjectName}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayStudents.map((s, i) => {
                    const included = selectedIds.has(s.student_id);
                    return (
                      <TableRow key={s.student_id} hover onClick={() => toggleStudent(s.student_id)}
                        sx={{ cursor: 'pointer', bgcolor: included ? 'rgba(26,122,60,0.05)' : i % 2 === 0 ? 'white' : '#f7f8fc' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={included} size="small" color="success"
                            onChange={() => toggleStudent(s.student_id)} onClick={e => e.stopPropagation()} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: included ? '#1a7a3c' : '#ccc', fontSize: 11, fontWeight: 700 }}>
                              {s.first_name?.[0]}{s.last_name?.[0]}
                            </Avatar>
                            <Typography fontSize={13} fontWeight={600}>
                              {s.last_name}, {s.first_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={s.admission_number} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize', fontSize: 12 }}>{s.gender}</TableCell>
                        <TableCell>
                          {included
                            ? <Chip label="✓ Included" size="small" color="success" sx={{ fontWeight: 700, fontSize: 10 }} />
                            : <Chip label="✗ Excluded" size="small" color="default" sx={{ fontSize: 10, color: 'text.disabled' }} />
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {displayStudents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary', fontStyle: 'italic' }}>
                        No students enrolled in this class for the selected year
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              size="large" sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' }, fontWeight: 700, minWidth: 180 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : `Save Register (${selectedIds.size} students)`}
            </Button>
          </Box>

          {/* Summary */}
          <Card sx={{ mt: 2, borderLeft: '4px solid #1a7a3c' }}>
            <CardContent sx={{ py: '12px !important' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Once saved:</strong> When you open Marks Entry and select <em>{subjectName}</em> for {classLabel},
                only these <strong>{selectedIds.size} students</strong> will appear in your entry sheet —
                across all assessments for the full {yearLabel?.year_label} academic year.
                You can update this list at any time.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}

      {!data && !loading && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Group sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
          <Typography variant="h6" color="text.disabled">Select a year, class and subject above to load the student register</Typography>
        </Box>
      )}
    </Box>
  );
}
